import isString from 'lodash-es/isString';
// TODO
import CustomEvents from './CustomEvents';
import commandFactory from './helper/factory/command';
import isFunction from 'lodash-es/isFunction';
import { eventNames, rejectMessages } from './utils/constant';

class Invoker {
  constructor() {
    // Undo stack
    this._undoStack = [];
    // Redo stack
    this._redoStack = [];
    // Lock-flag for executing command
    this._isLocked = false;
    // is slient
    this._isSilent = false;
  }
  // lock
  lock() {
    this._isLocked = true;
  }
  // Unlock this invoker
  unlock() {
    this._isLocked = false;
  }
  // Push undo stack
  pushUndoStack(command, isSilent) {
    this._undoStack.push(command);
    if (!isSilent) {
      this._fireUndoStackChanged();
    }
  }
  // Push redo stack
  pushRedoStack(command, isSilent) {
    this._redoStack.push(command);
    if (!isSilent) {
      this._fireRedoStackChanged();
    }
  }
  // fire UNDO_STACK_CHANGED event
  _fireUndoStackChanged() {
    this.fire(eventNames.UNDO_STACK_CHANGED, this._undoStack.length);
  }
  // fire REDO_STACK_CHANGED event
  _fireRedoStackChanged() {
    this.fire(eventNames.REDO_STACK_CHANGED, this._redoStack.length);
  }

  /**
   * Invoke command execution && push undo stack
   * @param {Command} command - Command
   * @param {boolean} [isRedo=false] - check if command is redo
   * @returns {Promise}
   * @private
   */
  _invokeExecution(command, isRedo = false) {
    this.lock(); // lock
    let { args } = command;
    if (!args) {
      args = []; // args
    }
    // 执行命令
    return command
      .execute(...args)
      .then((value) => {
        if (!this._isSilent) {
          // push && fire undo
          this.pushUndoStack(command);
          // fire AFTER_REDO
          this.fire(isRedo ? eventNames.AFTER_REDO : eventNames.EXECUTE_COMMAND, command);
        }
        // unlock
        this.unlock();
        // 如果命令设置了 executeCallback，则执行
        if (isFunction(command.executeCallback)) {
          command.executeCallback(value);
        }

        return value;
      })
      .catch((message) => {
        this.unlock();
        return Promise.reject(message);
      });
  }

  /**
   * Invoke command undo
   * @param {Command} command - Command
   * @returns {Promise}
   * @private
   */
  _invokeUndo(command) {
    this.lock();

    let { args } = command;
    if (!args) {
      args = [];
    }

    return command
      .undo(...args)
      .then((value) => {
        this.pushRedoStack(command);
        this.fire(eventNames.AFTER_UNDO, command);
        this.unlock();
        if (isFunction(command.undoCallback)) {
          command.undoCallback(value);
        }

        return value;
      })
      .catch((message) => {
        this.unlock();

        return Promise.reject(message);
      });
  }


  executeSilent(...args) {
    this._isSilent = true;

    return this.execute(...args, this._isSilent)
    .then(() => {
      this._isSilent = false;
    });
  }

  /**
   * Invoke command
   * Store the command to the undoStack
   * Clear the redoStack
   * @param {String} commandName - Command name
   * @param {...*} args - Arguments for creating command
   * @returns {Promise}
   */
  execute(...args) {
    if (this._isLocked) {
      return Promise.reject(rejectMessages.isLock);
    }

    let [command] = args;
    if (isString(command)) {
      command = commandFactory.create(...args);
    }

    return this._invokeExecution(command).then((value) => {
      this.clearRedoStack();

      return value;
    });
  }

  /**
   * Undo command
   * @returns {Promise}
   */
  undo() {
    let command = this._undoStack.pop();
    let promise;
    let message = '';

    if (command && this._isLocked) {
      this.pushUndoStack(command, true);
      command = null;
    }
    if (command) {
      if (this.isEmptyUndoStack()) {
        this._fireUndoStackChanged();
      }
      promise = this._invokeUndo(command);
    } else {
      message = rejectMessages.undo;
      if (this._isLocked) {
        message = `${message} Because ${rejectMessages.isLock}`;
      }
      promise = Promise.reject(message);
    }

    return promise;
  }

  /**
   * Redo command
   * @returns {Promise}
   */
  redo() {
    let command = this._redoStack.pop();
    let promise;
    let message = '';

    if (command && this._isLocked) {
      this.pushRedoStack(command, true);
      command = null;
    }
    if (command) {
      if (this.isEmptyRedoStack()) {
        this._fireRedoStackChanged();
      }
      promise = this._invokeExecution(command, true);
    } else {
      message = rejectMessages.redo;
      if (this._isLocked) {
        message = `${message} Because ${rejectMessages.isLock}`;
      }
      promise = Promise.reject(message);
    }

    return promise;
  }



  /**
   * Return whether the redoStack is empty
   * @returns {boolean}
   */
  isEmptyRedoStack() {
    return this._redoStack.length === 0;
  }

  /**
   * Return whether the undoStack is empty
   * @returns {boolean}
   */
  isEmptyUndoStack() {
    return this._undoStack.length === 0;
  }

  /**
   * Clear undoStack
   */
  clearUndoStack() {
    if (!this.isEmptyUndoStack()) {
      this._undoStack = [];
      this._fireUndoStackChanged();
    }
  }

  /**
   * Clear redoStack
   */
  clearRedoStack() {
    if (!this.isEmptyRedoStack()) {
      this._redoStack = [];
      this._fireRedoStackChanged();
    }
  }
}

Object.assign(Invoker.prototype, CustomEvents)

export default Invoker;
